import React, { memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { ActionButton } from 'src/components/shared/ActionButton'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { MenuActionItem } from 'src/interfaces/ui'
import { useSelector } from 'react-redux'
import { colors } from 'src/theme'
import { PlusMinor } from '@shopify/polaris-icons'
import { getMarkets } from 'src/selectors/market'
import { Market } from 'src/interfaces/market'

interface Props {
  selectedMarkets: Market[]
  onSelectMarket(market: Market): void
  onSelectAllMarkets?(markets: Market[]): void
}

const AddMarketButtonComponent = ({
  selectedMarkets,
  onSelectMarket,
  onSelectAllMarkets,
}: Props) => {
  const markets = useSelector(getMarkets)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const filteredMarkets = useMemo(
    () =>
      markets.filter(
        (item) =>
          !selectedMarkets ||
          !selectedMarkets.some((selectedMarket) => selectedMarket.title === item.title),
      ),
    [markets, selectedMarkets],
  )

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    if (filteredMarkets.length === 0) {
      return []
    }

    return [
      {
        label: 'All markets',
        onClick: () => {
          if (onSelectAllMarkets) {
            onSelectAllMarkets(filteredMarkets)
          } else {
            filteredMarkets.forEach(onSelectMarket)
          }

          setAnchorEl(null)
        },
        className: tw`border-solid border-0 border-b border-[#EDEDED]`,
      },
      ...filteredMarkets.map((item) => ({
        label: item.title,
        onClick: () => {
          onSelectMarket(item)
          setAnchorEl(null)
        },
      })),
    ]
  }, [filteredMarkets])

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <>
      <ActionButton
        className={tw`px-2 h-[28px] rounded`}
        selected={!!anchorEl}
        color="light"
        text="Add market"
        icon={<PlusMinor width={20} fill={colors.PRIMARY_PURPLE_500} />}
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        itemClassName={tw`min-w-[180px]`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleCloseMenu}
      />
    </>
  )
}

export const AddMarketButton = memo(AddMarketButtonComponent)
